import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { GpdCheckboxComponent } from 'src/app/components/gpd-checkbox/gpd-checkbox.component';
import {
  ShareholderRole,
  SocietyOrgChartEdge,
  SocietyOrgChartNode,
  SocietySalesModel,
} from 'src/app/services/society.service';
import {
  Area,
  vatAreas,
  applyBoxPositions,
  applyLineOverrides,
  BAND_LABEL_HEIGHT,
  BoxPositions,
  layoutOrgChart,
  LineOverrides,
  movableSegments,
  moveSegment,
  Point,
  toElbow,
  NODE_HEIGHT,
  NODE_WIDTH,
  OrgChartLayout,
  PlacedBand,
  PlacedEdge,
  PlacedNode,
} from './org-chart-layout';
import { KindColors, LINE_COLORS, lineColor, LineColors, LineKind } from './org-chart-colors';
import { ColorPanelComponent } from 'src/app/components/color-panel/color-panel.component';

interface ChartBox extends PlacedNode {
  node: SocietyOrgChartNode;
  matches: boolean;
}

interface ChartLine extends PlacedEdge {
  id: string;
  /** The line as an elbow connector (see toElbow): what its handles move. */
  elbow: Point[];
  path: string;
  /** Name of the owner, shown in the label while the held box is selected. */
  ownerName: string;
  role: ShareholderRole | null;
  share: number | null;
  /** Where the share label sits: on the last segment, just above the held box. */
  labelX: number;
  labelY: number;
}

interface ChartBand extends PlacedBand {
  label: string;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 2;
const PADDING = 32;
/** Legend and buttons float along the top: fitting keeps the chart below them. */
const TOP_OVERLAY = 64;
/** Boxes moved by hand, per browser (example data apart, so trying it out leaves no trace). */
const POSITIONS_KEY = 'societyOrgChart.positions';
/** Lines reshaped by hand, kept the same way. */
const LINES_KEY = 'societyOrgChart.lines';
/** What the boxes and lines show ("Anzeige"), per browser. */
const DISPLAY_KEY = 'societyOrgChart.display';
/** Colours of single lines (part of the arrangement) and of kinds of lines (legend). */
const LINE_COLORS_KEY = 'societyOrgChart.lineColors';
const KIND_COLORS_KEY = 'societyOrgChart.kindColors';
/** Own colours added with "+", newest first; for the real and the example data alike. */
const CUSTOM_COLORS_KEY = 'societyOrgChart.customColors';
const MAX_CUSTOM_COLORS = 8;

/** Parts of the org chart the user can show or hide. */
export interface OrgChartDisplay {
  directors: boolean;
  accountant: boolean;
  shares: boolean;
  vatArea: boolean;
  bands: boolean;
}

const DEFAULT_DISPLAY: OrgChartDisplay = { directors: true, accountant: true, shares: true, vatArea: true, bands: true };

/**
 * Org chart of the Gesellschaften, after the whiteboard: owners above what they hold, the share
 * on each line (solid: Komplementär, dashed: Kommanditist), the Gesellschaften grouped in bands
 * by Verkaufsmodell, and the umsatzsteuerpflichtige ones marked green.
 * Self-built (no diagram library): HTML boxes and an SVG layer for the lines inside one
 * canvas that is panned by dragging and zoomed with the wheel or the buttons. Boxes can be
 * dragged to another place; that is kept in this browser until "Reset arrangement".
 */
@Component({
  selector: 'app-society-org-chart',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ColorPanelComponent, GpdButtonComponent, GpdCheckboxComponent, SvgIconComponent],
  templateUrl: './society-org-chart.component.html',
  styleUrls: ['./society-org-chart.component.css'],
})
export class SocietyOrgChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() nodes: SocietyOrgChartNode[] = [];
  @Input() edges: SocietyOrgChartEdge[] = [];
  /** Band labels and their order. */
  @Input() salesModels: SocietySalesModel[] = [];
  /** Highlights the boxes whose name contains it and dims the others. */
  @Input() searchTerm = '';
  /** Example data: the boxes don't link anywhere. */
  @Input() demo = false;

  @ViewChild('viewport') viewport?: ElementRef<HTMLElement>;

  readonly nodeHeight = NODE_HEIGHT;
  readonly bandLabelHeight = BAND_LABEL_HEIGHT;


  readonly hintZoomIn = $localize`:@@zoom-in:Zoom in`;
  readonly hintZoomOut = $localize`:@@zoom-out:Zoom out`;
  readonly hintFit = $localize`:@@orgChartFit:Fit to screen`;
  /** Short for Geschäftsführung, before the names in a box. */
  readonly directorsLabel = $localize`:@@orgChartDirectorsShort:MD:`;
  /** Short for zuständiger Buchhalter and its Vertretung, before the names in a box. */
  readonly accountantLabel = $localize`:@@orgChartAccountantShort:Acc.:`;
  readonly accountantDeputyLabel = $localize`:@@orgChartAccountantDeputyShort:Dep. acc.:`;
  readonly generalPartnerLabel = $localize`:@@societyGeneralPartner:General partner`;
  readonly limitedPartnerLabel = $localize`:@@societyLimitedPartner:Limited partner`;
  readonly hintOpen = $localize`:@@orgChartOpen:Open`;
  readonly hintExportPdf = $localize`:@@orgChartExportPdf:Export as PDF`;
  readonly hintResetArrangement = $localize`:@@orgChartResetArrangement:Reset arrangement`;
  readonly hintMoveSegment = $localize`:@@orgChartMoveSegment:Drag to move this part of the line`;
  readonly hintDisplay = $localize`:@@orgChartDisplay:Display`;
  readonly vatAreaLabel = $localize`:@@orgChartVatAreaLabel:VAT`;
  readonly hintLineColor = $localize`:@@orgChartLineColor:Line colour`;
  readonly hintStandardColor = $localize`:@@orgChartStandardColor:Standard`;
  readonly hintChangeColor = $localize`:@@orgChartChangeColor:Change colour`;
  readonly hintResetKindColors = $localize`:@@orgChartResetLineColors:Reset line colours`;
  readonly hintAddColor = $localize`:@@orgChartAddColor:Own colour`;
  /** Own-colour panel ("+") open in the line's toolbar or in a legend picker. */
  customPanel: 'line' | LineKind | null = null;
  /** Last colour set in the open panel: becomes an own colour when the panel is closed. */
  private panelColor: string | null = null;
  /** Own colours, after the palette in every picker. */
  customColors: string[] = this.loadCustomColors();

  readonly lineColorOptions = LINE_COLORS;
  /** Colour of single lines (from the line's toolbar) and of kinds of lines (from the legend). */
  lineColors: LineColors = {};
  kindColors: KindColors = {};
  /** Kind of line whose colour is being picked in the legend. */
  kindColorMenu: LineKind | null = null;

  /** "Anzeige": what the boxes and lines show; everything by default. */
  display: OrgChartDisplay = this.loadDisplay();
  displayMenuOpen = false;
  readonly displayOptions: { key: keyof OrgChartDisplay; label: string }[] = [
    { key: 'directors', label: $localize`:@@orgChartShowDirectors:Management` },
    { key: 'accountant', label: $localize`:@@orgChartShowAccountant:Accountant and deputy` },
    { key: 'shares', label: $localize`:@@orgChartShowShares:Shares on the lines` },
    { key: 'vatArea', label: $localize`:@@orgChartShowVatArea:VAT area` },
    { key: 'bands', label: $localize`:@@orgChartShowBands:Portfolio assignments` },
  ];
  exportingPdf = false;
  readonly typeLabels: Record<SocietyOrgChartNode['type'], string> = {
    society: $localize`:@@orgChartSociety:Society`,
    company: $localize`:@@orgChartCompany:Company`,
    person: $localize`:@@orgChartPerson:Person`,
  };

  /** As computed; `layout` is this with the boxes moved by hand. */
  private autoLayout: OrgChartLayout = { nodes: [], edges: [], bands: [], width: 0, height: 0 };
  layout: OrgChartLayout = { nodes: [], edges: [], bands: [], width: 0, height: 0 };
  /** Frames around the umsatzsteuerpflichtigen boxes ("USt."), none around any other box. */
  vatFrames: Area[] = [];
  positions: BoxPositions = {};
  lineOverrides: LineOverrides = {};
  /** Clicked line: shows a handle on each segment that can be moved. */
  selectedLineId: string | null = null;
  /** A segment of a line being moved by its handle. */
  segmentDrag: {
    lineId: string;
    index: number;
    pointerId: number;
    start: number;
    origin: number;
    base: Point[];
    moved: boolean;
  } | null = null;
  boxes: ChartBox[] = [];
  lines: ChartLine[] = [];
  bands: ChartBand[] = [];

  scale = 1;
  offsetX = 0;
  offsetY = 0;

  /**
   * Clicked box: it and everything directly connected to it stay strong, the rest fades, so the
   * lines can be followed (e.g. whose the two 50 % are). Opening is the arrow next to it.
   */
  selectedKey: string | null = null;
  private relatedKeys = new Set<string>();
  private relatedLines = new Set<string>();

  drag: { pointerId: number; startX: number; startY: number; originX: number; originY: number; moved: boolean } | null =
    null;
  /** A box being dragged to another place (instead of panning the canvas). */
  boxDrag: {
    key: string;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null = null;
  private resizeObserver?: ResizeObserver;
  private fitted = false;

  constructor(private router: Router) {}

  get searching(): boolean {
    return !!this.searchTerm?.trim();
  }

  get transform(): string {
    return `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // New data (e.g. example data switched on or off): fit again; a search keeps the zoom.
    if (changes['nodes'] && !changes['nodes'].firstChange) this.fitted = false;
    if (changes['demo'] || changes['nodes']?.firstChange) {
      this.positions = this.load<BoxPositions>(this.storageKey(POSITIONS_KEY));
      this.lineOverrides = this.load<LineOverrides>(this.storageKey(LINES_KEY));
      this.lineColors = this.load<LineColors>(this.storageKey(LINE_COLORS_KEY));
      this.kindColors = this.load<KindColors>(this.storageKey(KIND_COLORS_KEY));
    }
    this.relayout();
    // Keep the selection while its box is still there (e.g. while searching).
    this.select(this.boxes.some(b => b.key === this.selectedKey) ? this.selectedKey : null);
    // Fit once the data is there; later searches keep the user's zoom.
    if (!this.fitted && this.boxes.length && this.viewport) {
      setTimeout(() => this.fit());
    }
  }

  /** Layout anew: the box widths depend on what they show. */
  private relayout(): void {
    const bandKey = (id: number | null | undefined) => (id != null ? String(id) : null);
    this.autoLayout = layoutOrgChart(
      this.nodes.map(n => ({
        key: n.key,
        band: n.type === 'society' ? bandKey(n.salesModelId) : null,
        width: this.boxWidthFor(n),
      })),
      this.edges.map(e => ({ from: e.from, to: e.to, style: e.role })),
      this.salesModels.map(m => String(m.id))
    );
    this.buildView();
  }

  // ---- display ("Anzeige") ----

  private loadDisplay(): OrgChartDisplay {
    try {
      return { ...DEFAULT_DISPLAY, ...JSON.parse(localStorage.getItem(DISPLAY_KEY) ?? '{}') };
    } catch {
      return { ...DEFAULT_DISPLAY };
    }
  }

  toggleDisplay(key: keyof OrgChartDisplay, value: boolean): void {
    this.display = { ...this.display, [key]: value };
    try {
      localStorage.setItem(DISPLAY_KEY, JSON.stringify(this.display));
    } catch {
      // Storage blocked: the choice holds until the page is left.
    }
    this.relayout();
    this.select(this.selectedKey);
  }

  /** Clicks outside the menus close them. */
  @HostListener('document:click')
  closeDisplayMenu(): void {
    this.displayMenuOpen = false;
    this.kindColorMenu = null;
    this.closeCustomPanel();
  }

  // ---- line colours ----

  /** A line's colour: its own, else its kind's, else null (the standard look). */
  colorOf(line: ChartLine): string | null {
    return lineColor(line.id, line.role, this.lineColors, this.kindColors);
  }

  kindColor(kind: LineKind): string | null {
    return this.kindColors[kind] ?? null;
  }

  get hasKindColors(): boolean {
    return Object.keys(this.kindColors).length > 0;
  }

  setLineColor(color: string | null): void {
    const id = this.selectedLineId;
    if (!id) return;
    const { [id]: _, ...rest } = this.lineColors;
    this.lineColors = color ? { ...rest, [id]: color } : rest;
    this.save();
  }

  /** For "+": where an own colour goes. */
  readonly applyToSelectedLine = (color: string) => this.setLineColor(color);
  setKindColorOf(kind: LineKind): (color: string) => void {
    return color => this.setKindColor(kind, color, true);
  }

  toggleKindColorMenu(kind: LineKind): void {
    this.displayMenuOpen = false;
    this.closeCustomPanel();
    this.kindColorMenu = this.kindColorMenu === kind ? null : kind;
  }

  /** `keepOpen`: from the own-colour panel, which stays while the colour is being found. */
  setKindColor(kind: LineKind, color: string | null, keepOpen = false): void {
    const { [kind]: _, ...rest } = this.kindColors;
    this.kindColors = color ? { ...rest, [kind]: color } : rest;
    if (!keepOpen) {
      this.closeCustomPanel();
      this.kindColorMenu = null;
    }
    this.saveKindColors();
  }

  private loadCustomColors(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(CUSTOM_COLORS_KEY) ?? '[]');
      return Array.isArray(stored) ? stored.filter(c => /^#[0-9a-f]{6}$/i.test(c)).slice(0, MAX_CUSTOM_COLORS) : [];
    } catch {
      return [];
    }
  }

  /**
   * "+" in a picker: the colour from the system's colour dialog is used right away and kept as
   * an own colour (newest first, the oldest dropped beyond MAX_CUSTOM_COLORS).
   */
  addCustomColor(value: string, apply?: (color: string) => void): void {
    const color = value.toLowerCase();
    if (!LINE_COLORS.some(c => c.value === color)) {
      this.customColors = [color, ...this.customColors.filter(c => c !== color)].slice(0, MAX_CUSTOM_COLORS);
      try {
        localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(this.customColors));
      } catch {
        // Storage blocked: the colour holds until the page is left.
      }
    }
    apply?.(color);
  }

  toggleCustomPanel(target: 'line' | LineKind): void {
    const opening = this.customPanel !== target;
    this.closeCustomPanel();
    if (opening) this.customPanel = target;
  }

  /** While the panel is open a colour is only applied (to the line or the kind). */
  onPanelColor(color: string, apply: (color: string) => void): void {
    this.panelColor = color;
    apply(color);
  }

  /** The colour being chosen, shown in the "+" dot while its panel is open; null before any. */
  panelPreview(target: 'line' | LineKind): string | null {
    return this.customPanel === target ? this.panelColor : null;
  }

  /** "+" on the preview colour: white on dark colours, dark on light ones. */
  plusColor(background: string | null): string | null {
    if (!background) return null;
    const n = parseInt(background.slice(1), 16);
    const luminance = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return luminance > 0.6 ? '#1f1f1f' : '#ffffff';
  }

  /** Closing the panel keeps its last colour as one own-colour dot, not every one tried on the way. */
  closeCustomPanel(): void {
    if (this.panelColor) this.addCustomColor(this.panelColor);
    this.panelColor = null;
    this.customPanel = null;
  }

  resetKindColors(): void {
    this.kindColors = {};
    this.saveKindColors();
  }

  private saveKindColors(): void {
    try {
      localStorage.setItem(this.storageKey(KIND_COLORS_KEY), JSON.stringify(this.kindColors));
    } catch {
      // Storage blocked: the colours hold until the page is left.
    }
  }

  /**
   * Where the selected line's colour toolbar sits, in viewport pixels (so it keeps its size at
   * any zoom): above the middle of its longest horizontal stretch, or beside a straight line.
   */
  get lineToolbarPosition(): { left: number; top: number } | null {
    const line = this.selectedLine;
    if (!line) return null;
    const points = line.points;
    let anchor = { x: points[0].x, y: (points[0].y + points[points.length - 1].y) / 2 };
    let longest = 0;
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      if (a.y === b.y && Math.abs(b.x - a.x) > longest) {
        longest = Math.abs(b.x - a.x);
        anchor = { x: (a.x + b.x) / 2, y: a.y };
      }
    }
    return { left: this.offsetX + anchor.x * this.scale, top: this.offsetY + anchor.y * this.scale - 12 };
  }

  /** Geschäftsführung as shown in the box: none while hidden. */
  directorsOf(node: SocietyOrgChartNode): string[] {
    return this.display.directors ? node.managingDirectors ?? [] : [];
  }


  /** Boxes, lines and bands from the computed layout and the boxes moved by hand. */
  private buildView(): void {
    this.layout = applyLineOverrides(applyBoxPositions(this.autoLayout, this.positions), this.lineOverrides);
    const byKey = new Map(this.nodes.map(n => [n.key, n]));
    const bandNames = new Map(this.salesModels.map(m => [String(m.id), m.name]));
    this.bands = this.layout.bands.map(b => ({ ...b, label: bandNames.get(b.band) ?? '' }));
    const term = this.searchTerm?.trim().toLowerCase() ?? '';
    this.boxes = this.layout.nodes.map(p => {
      const node = byKey.get(p.key)!;
      return { ...p, node, matches: !term || (node.name ?? '').toLowerCase().includes(term) };
    });
    this.vatFrames = this.display.vatArea
      ? vatAreas(
          this.boxes.filter(b => b.node.vatLiable),
          this.boxes.filter(b => !b.node.vatLiable)
        )
      : [];
    const edgeData = new Map(this.edges.map(e => [`${e.from}>${e.to}`, e]));
    const names = new Map(this.nodes.map(n => [n.key, n.name]));
    this.lines = this.layout.edges.map(e => {
      const end = e.points[e.points.length - 1];
      const data = edgeData.get(`${e.from}>${e.to}`);
      return {
        ...e,
        id: `${e.from}>${e.to}`,
        ownerName: names.get(e.from) ?? '',
        path: e.points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' '),
        // A reshaped line is already one; the others become one when first touched.
        elbow: this.lineOverrides[`${e.from}>${e.to}`] && e.points === this.lineOverrides[`${e.from}>${e.to}`].points ? e.points : toElbow(e.points),
        role: data?.role ?? null,
        share: data?.share ?? null,
        labelX: end.x,
      };
    });
  }

  // ---- arrangement by hand ----

  get hasArrangement(): boolean {
    return this.layout !== this.autoLayout || Object.keys(this.lineColors).length > 0;
  }

  private load<T>(key: string): T {
    try {
      return JSON.parse(localStorage.getItem(key) ?? '{}') ?? ({} as T);
    } catch {
      return {} as T;
    }
  }

  private save(): void {
    try {
      localStorage.setItem(this.storageKey(POSITIONS_KEY), JSON.stringify(this.positions));
      localStorage.setItem(this.storageKey(LINES_KEY), JSON.stringify(this.lineOverrides));
      localStorage.setItem(this.storageKey(LINE_COLORS_KEY), JSON.stringify(this.lineColors));
    } catch {
      // Storage blocked (e.g. private window): the arrangement just isn't kept.
    }
  }

  private storageKey(key: string): string {
    return this.demo ? `${key}.demo` : key;
  }

  resetArrangement(): void {
    this.positions = {};
    this.lineOverrides = {};
    this.lineColors = {};
    this.selectedLineId = null;
    this.save();
    this.buildView();
    this.select(this.selectedKey);
    this.fit();
  }

  onBoxPointerDown(box: ChartBox, e: PointerEvent): void {
    if (e.button !== 0) return;
    e.stopPropagation(); // drags the box, not the canvas
    this.boxDrag = { key: box.key, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: box.x, originY: box.y, moved: false };
  }

  ngAfterViewInit(): void {
    if (this.viewport && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.fitted && this.boxes.length) this.fit();
      });
      this.resizeObserver.observe(this.viewport.nativeElement);
    }
    if (this.boxes.length) setTimeout(() => this.fit());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  // ---- zoom ----

  fit(): void {
    const el = this.viewport?.nativeElement;
    if (!el || !this.layout.width) return;
    const { clientWidth, clientHeight } = el;
    if (!clientWidth || !clientHeight) return;
    const usableHeight = clientHeight - TOP_OVERLAY - PADDING;
    // The USt frames may reach above and left of the boxes.
    const frames = this.vatFrames;
    const minX = Math.min(0, ...frames.map(f => f.x));
    const minY = Math.min(0, ...frames.map(f => f.y));
    const width = Math.max(this.layout.width, ...frames.map(f => f.x + f.width)) - minX;
    const height = Math.max(this.layout.height, ...frames.map(f => f.y + f.height)) - minY;
    const scale = Math.min((clientWidth - 2 * PADDING) / width, usableHeight / height, 1);
    this.scale = Math.max(MIN_SCALE, scale);
    this.offsetX = (clientWidth - width * this.scale) / 2 - minX * this.scale;
    this.offsetY = TOP_OVERLAY + Math.max(0, (usableHeight - height * this.scale) / 2) - minY * this.scale;
    this.fitted = true;
  }

  zoomIn(): void {
    this.zoomAround(1.2);
  }

  zoomOut(): void {
    this.zoomAround(1 / 1.2);
  }

  onWheel(e: WheelEvent): void {
    e.preventDefault();
    const rect = this.viewport!.nativeElement.getBoundingClientRect();
    this.zoomAround(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX - rect.left, e.clientY - rect.top);
  }

  /** Zooms keeping the point under (px, py) in place; without a point, the viewport centre. */
  private zoomAround(factor: number, px?: number, py?: number): void {
    const el = this.viewport?.nativeElement;
    if (!el) return;
    const cx = px ?? el.clientWidth / 2;
    const cy = py ?? el.clientHeight / 2;
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale * factor));
    const ratio = next / this.scale;
    this.offsetX = cx - (cx - this.offsetX) * ratio;
    this.offsetY = cy - (cy - this.offsetY) * ratio;
    this.scale = next;
  }

  // ---- pan ----

  onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    this.drag = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, originX: this.offsetX, originY: this.offsetY, moved: false };
  }

  onPointerMove(e: PointerEvent): void {
    if (this.segmentDrag && e.pointerId === this.segmentDrag.pointerId) {
      this.moveLineSegment(e);
      return;
    }
    if (this.boxDrag && e.pointerId === this.boxDrag.pointerId) {
      this.moveBox(e);
      return;
    }
    if (!this.drag || e.pointerId !== this.drag.pointerId) return;
    const dx = e.clientX - this.drag.startX;
    const dy = e.clientY - this.drag.startY;
    // A few pixels of jitter still count as a click on a box.
    if (!this.drag.moved && Math.hypot(dx, dy) < 4) return;
    if (!this.drag.moved) {
      this.drag.moved = true;
      this.viewport?.nativeElement.setPointerCapture(e.pointerId);
    }
    this.offsetX = this.drag.originX + dx;
    this.offsetY = this.drag.originY + dy;
  }

  /** Follows the pointer (in chart coordinates, so at any zoom); never off the top or left edge. */
  private moveBox(e: PointerEvent): void {
    const drag = this.boxDrag!;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;
    if (!drag.moved) {
      drag.moved = true;
      this.viewport?.nativeElement.setPointerCapture(e.pointerId);
    }
    this.positions = {
      ...this.positions,
      [drag.key]: { x: Math.max(0, drag.originX + dx / this.scale), y: Math.max(0, drag.originY + dy / this.scale) },
    };
    this.buildView();
  }

  onPointerUp(e: PointerEvent): void {
    if (this.segmentDrag) {
      if (this.segmentDrag.moved) {
        this.viewport?.nativeElement.releasePointerCapture(e.pointerId);
        this.save();
      }
      setTimeout(() => (this.segmentDrag = null));
      return;
    }
    if (this.boxDrag) {
      if (this.boxDrag.moved) {
        this.viewport?.nativeElement.releasePointerCapture(e.pointerId);
        // Its lines go back to the automatic routing between the new places.
        const key = this.boxDrag.key;
        this.lineOverrides = Object.fromEntries(
          Object.entries(this.lineOverrides).filter(([id]) => !id.split('>').includes(key))
        );
        this.save();
      }
      // Keep it until the click that follows the drag has been swallowed.
      setTimeout(() => (this.boxDrag = null));
      return;
    }
    if (this.drag?.moved) this.viewport?.nativeElement.releasePointerCapture(e.pointerId);
    // Keep the flag until the click that follows a drag has been swallowed.
    setTimeout(() => (this.drag = null));
  }

  // ---- lines reshaped by hand ----

  get selectedLine(): ChartLine | undefined {
    return this.selectedLineId ? this.lines.find(l => l.id === this.selectedLineId) : undefined;
  }

  /** Handles of the selected line: one in the middle of each segment that can be moved. */
  get lineHandles(): { index: number; x: number; y: number; vertical: boolean }[] {
    const line = this.selectedLine;
    if (!line) return [];
    return movableSegments(line.elbow).map(index => {
      const a = line.elbow[index];
      const b = line.elbow[index + 1];
      return { index, x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, vertical: index % 2 === 0 };
    });
  }

  onLineClick(line: ChartLine, e: MouseEvent): void {
    e.stopPropagation();
    if (this.drag?.moved) return;
    this.select(null);
    this.selectedLineId = line.id;
    this.closeCustomPanel();
  }

  onHandlePointerDown(index: number, e: PointerEvent): void {
    const line = this.selectedLine;
    if (e.button !== 0 || !line) return;
    e.stopPropagation(); // moves the segment, not the canvas
    const vertical = index % 2 === 0;
    this.segmentDrag = {
      lineId: line.id,
      index,
      pointerId: e.pointerId,
      start: vertical ? e.clientX : e.clientY,
      origin: vertical ? line.elbow[index].x : line.elbow[index].y,
      base: line.elbow,
      moved: false,
    };
  }

  /** A vertical segment follows the pointer sideways, a horizontal one up and down. */
  private moveLineSegment(e: PointerEvent): void {
    const drag = this.segmentDrag!;
    const vertical = drag.index % 2 === 0;
    const delta = (vertical ? e.clientX : e.clientY) - drag.start;
    if (!drag.moved && Math.abs(delta) < 3) return;
    if (!drag.moved) {
      drag.moved = true;
      this.viewport?.nativeElement.setPointerCapture(e.pointerId);
    }
    this.lineOverrides = {
      ...this.lineOverrides,
      [drag.lineId]: { points: moveSegment(drag.base, drag.index, drag.origin + delta / this.scale) },
    };
    this.buildView();
  }

  // ---- select & open ----

  get selectedBox(): ChartBox | undefined {
    return this.selectedKey ? this.boxes.find(b => b.key === this.selectedKey) : undefined;
  }

  onBoxClick(box: ChartBox, e: MouseEvent): void {
    e.stopPropagation();
    if (this.drag?.moved || this.boxDrag?.moved) return;
    this.select(box.key);
  }

  /** A click on the empty canvas (not the end of a drag) clears the selection. */
  onViewportClick(): void {
    if (this.drag?.moved || this.segmentDrag?.moved) return;
    this.select(null);
  }

  @HostListener('document:keydown.escape')
  clearSelection(): void {
    this.select(null);
    this.closeDisplayMenu();
  }

  private select(key: string | null): void {
    if (this.customPanel === 'line') this.closeCustomPanel();
    this.selectedKey = key;
    this.selectedLineId = null;
    this.relatedKeys = new Set(key ? [key] : []);
    this.relatedLines = new Set();
    if (!key) return;
    for (const line of this.lines) {
      if (line.from === key || line.to === key) {
        this.relatedLines.add(line.id);
        this.relatedKeys.add(line.from);
        this.relatedKeys.add(line.to);
      }
    }
  }

  isRelated(box: ChartBox): boolean {
    return this.relatedKeys.has(box.key);
  }

  isLineRelated(line: ChartLine): boolean {
    return this.relatedLines.has(line.id);
  }

  /** Box faded: by the selection if there is one, otherwise by the search. */
  isDimmed(box: ChartBox): boolean {
    if (this.selectedKey) return !this.isRelated(box);
    return this.searching && !box.matches;
  }

  isHighlighted(box: ChartBox): boolean {
    if (this.selectedKey) return false;
    return this.searching && box.matches;
  }

  /** Label text: the share; while its box is selected, with the owner's name. */
  hasShare(line: ChartLine): boolean {
    return line.share != null && line.share > 0;
  }

  lineLabel(line: ChartLine): string {
    const value = this.formatShare(line.share);
    return this.selectedKey === line.to ? `${line.ownerName} · ${value}` : value;
  }

  /**
   * With the owner's name the labels into the selected box get long, so they step down one
   * under the other (left line on top) instead of running into each other.
   */
  labelTop(line: ChartLine): number {
    if (this.selectedKey !== line.to) return line.labelY;
    const siblings = this.lines.filter(l => l.to === line.to && this.hasShare(l)).sort((a, b) => a.labelX - b.labelX);
    return line.labelY - 12 + siblings.indexOf(line) * 26;
  }

  /** Who holds what, on hover over a label. */
  lineTitle(line: ChartLine): string {
    const role =
      line.role === 'general_partner' ? this.generalPartnerLabel : line.role === 'limited_partner' ? this.limitedPartnerLabel : '';
    return [line.ownerName, role, line.share != null ? this.formatShare(line.share) : ''].filter(Boolean).join(' · ');
  }

  /**
   * The whole chart as a vector PDF, without selection or highlighting. jsPDF is loaded on click:
   * this page is in the initial bundle.
   */
  async exportPdf(): Promise<void> {
    if (!this.boxes.length || this.exportingPdf) return;
    this.exportingPdf = true;
    try {
      const [{ jsPDF }, { buildOrgChartPdf }] = await Promise.all([import('jspdf'), import('./org-chart-pdf')]);
      const today = new Date();
      const doc = buildOrgChartPdf(jsPDF, {
        title: $localize`:@@orgChartPdfTitle:Org chart of the societies`,
        subtitle: $localize`:@@orgChartPdfAsOf:As of ${today.toLocaleDateString()}:date:`,
        layout: this.layout,
        boxes: this.boxes.map(b => ({
          key: b.key,
          x: b.x,
          y: b.y,
          width: b.width,
          type: b.node.type,
          name: b.node.name ?? '',
          caption: this.directorsOf(b.node).length
            ? `${this.directorsLabel} ${this.directorsOf(b.node).join(', ')}`
            : this.typeLabels[b.node.type],
          details: this.accountantLine(b.node),
        })),
        lines: this.lines.map(l => ({
          points: l.points,
          role: l.role,
          color: this.colorOf(l),
          label: this.display.shares && this.hasShare(l) ? this.formatShare(l.share) : null,
          labelX: l.labelX,
          labelY: l.labelY,
        })),
        bands: this.display.bands ? this.bands : [],
        vatAreas: this.vatFrames.map(f => ({ ...f, label: this.vatAreaLabel })),
        legend: {
          generalPartner: this.generalPartnerLabel,
          limitedPartner: this.limitedPartnerLabel,
          shareholder: $localize`:@@societyShareholder:Shareholder`,
        },
        legendColors: this.kindColors,
      });
      const date = today.toISOString().slice(0, 10);
      doc.save(`${$localize`:@@orgChartPdfFileName:Org-chart-societies`}_${date}.pdf`);
    } catch (error) {
      console.error('Org chart PDF export failed', error);
      AppGlobals.ShowErrorMessage($localize`:@@orgChartPdfFailed:The org chart could not be exported. Please try again.`);
    } finally {
      this.exportingPdf = false;
    }
  }

  open(box: ChartBox): void {
    if (this.drag?.moved || this.boxDrag?.moved || this.demo) return;
    this.router.navigate(['/contacts', box.node.type, box.node.id]);
  }

  private measureContext?: CanvasRenderingContext2D | null;
  private bodyFont = { size: '14px', family: 'sans-serif' };

  private textWidth(text: string, font: string): number {
    this.measureContext!.font = font;
    return Math.ceil(this.measureContext!.measureText(text).width);
  }

  /**
   * Wide enough for the whole name and the whole Geschäftsführung, each on one line (neither is
   * cut off): measured with the box's fonts, plus padding, icon and the USt mark. At least NODE_WIDTH.
   */
  private boxWidthFor(node: SocietyOrgChartNode): number {
    if (this.measureContext === undefined) {
      this.measureContext = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
      if (this.measureContext) {
        const body = getComputedStyle(document.body);
        this.bodyFont = { size: body.fontSize || '14px', family: body.fontFamily || 'sans-serif' };
      }
    }
    if (!this.measureContext) return NODE_WIDTH;
    const { size, family } = this.bodyFont;
    const directors = this.directorsOf(node);
    // Labels in bold, names regular, 16px between the parts (see .org-chart-box-accountant).
    const accountantParts = this.accountantParts(node);
    const accountant = accountantParts.length
      ? accountantParts.reduce(
          (sum, p) => sum + this.textWidth(`${p.label} `, `600 12px ${family}`) + this.textWidth(p.name, `400 12px ${family}`),
          (accountantParts.length - 1) * 16
        )
      : 0;
    const name = Math.max(
      this.textWidth(node.name ?? '', `500 ${size} ${family}`),
      directors.length
        ? this.textWidth(`${this.directorsLabel} `, `600 12px ${family}`) + this.textWidth(directors.join(', '), `400 12px ${family}`)
        : 0,
      accountant
    );
    // padding 12+12, border 2, a little air 6
    let chrome = 24 + 2 + 6;
    if (node.type !== 'society') chrome += 20 + 8; // icon + gap
    return Math.max(NODE_WIDTH, name + chrome);
  }

  /** Buchhaltung of a Gesellschaft: Buchhalter and Vertretung, each with its short label. */
  accountantParts(node: SocietyOrgChartNode): { label: string; name: string }[] {
    if (!this.display.accountant) return [];
    return [
      node.accountant ? { label: this.accountantLabel, name: node.accountant } : null,
      node.accountantDeputy ? { label: this.accountantDeputyLabel, name: node.accountantDeputy } : null,
    ].filter((p): p is { label: string; name: string } => !!p);
  }

  /** The same as one line of text (PDF, tooltip), the parts set apart by a wide gap; null without either. */
  accountantLine(node: SocietyOrgChartNode): string | null {
    const parts = this.accountantParts(node).map(p => `${p.label} ${p.name}`);
    return parts.length ? parts.join('     ') : null;
  }

  /** Full name, Geschäftsführung and Buchhaltung on hover. */
  boxTitle(box: ChartBox): string {
    const directors = box.node.managingDirectors ?? [];
    return [box.node.name, directors.length ? `${this.directorsLabel} ${directors.join(', ')}` : null, this.accountantLine(box.node)]
      .filter(Boolean)
      .join('\n');
  }

  formatShare(share: number | null): string {
    return share == null ? '' : `${share.toLocaleString(undefined, { maximumFractionDigits: 2 })} %`;
  }

  trackBox = (_: number, box: ChartBox) => box.key;
  trackLine = (_: number, line: ChartLine) => line.id;
}
